<template>
    <div
        class="block-user-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="block-user-modal-title"
        aria-describedby="block-user-modal-description"
        @click.self="closePopup"
    >
        <div
            ref="modalDialog"
            class="block-user-modal__dialog"
        >

            <div class="block-user-modal__body">
                <div
                    class="block-user-modal__icon"
                    aria-hidden="true"
                >
                    <i class="fa fa-ban"></i>
                </div>

                <p
                    id="block-user-modal-description"
                    class="block-user-modal__message"
                >
                    Are you sure you want to block
                    <strong>
                        {{ displayUserName }}
                    </strong>?
                </p>

                <p class="block-user-modal__description">
                    Blocked contacts will no longer be able
                    to send you messages.
                </p>
            </div>

            <footer class="block-user-modal__footer">
                <button
                    type="button"
                    class="
                        block-user-modal__button
                        block-user-modal__button--cancel
                    "
                    :disabled="isSubmitting"
                    @click="closePopup"
                >
                    Cancel
                </button>

                <button
                    ref="confirmButton"
                    type="button"
                    class="
                        block-user-modal__button
                        block-user-modal__button--confirm
                    "
                    :disabled="isSubmitting"
                    @click="confirmBlockedUser"
                >
                    <i
                        v-if="isSubmitting"
                        class="fa fa-spinner fa-spin"
                        aria-hidden="true"
                    ></i>

                    <span>
                        {{
                            isSubmitting
                                ? 'Blocking...'
                                : 'Block'
                        }}
                    </span>
                </button>
            </footer>
        </div>
    </div>
</template>

<script>
import { bus } from '@/main';
import toast from '@/services/toast';

export default {
    name: 'UserBlockedPopup',

    props: {
        blockedUserId: {
            type: [String, Number],
            required: true
        },

        blockedUserEmail: {
            type: String,
            default: ''
        },

        blockedUserName: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            isSubmitting: false,
            previouslyFocusedElement: null
        };
    },

    computed: {
        displayUserName() {
            const name = String(
                this.blockedUserName || ''
            ).trim();

            if (name) {
                return name;
            }

            const email = String(
                this.blockedUserEmail || ''
            ).trim();

            return email || 'this user';
        },

        hasValidUserId() {
            return (
                this.blockedUserId !== null &&
                this.blockedUserId !== undefined &&
                String(this.blockedUserId).trim() !== ''
            );
        }
    },

    mounted() {
        this.previouslyFocusedElement =
            document.activeElement;

        document.addEventListener(
            'keydown',
            this.handleKeydown
        );

        document.body.classList.add(
            'body_right_scroll_remove'
        );

        this.$nextTick(() => {
            if (this.$refs.confirmButton) {
                this.$refs.confirmButton.focus();
            }
        });
    },

    beforeDestroy() {
        document.removeEventListener(
            'keydown',
            this.handleKeydown
        );

        document.body.classList.remove(
            'body_right_scroll_remove'
        );
    },

    methods: {
        async confirmBlockedUser() {
            if (this.isSubmitting) {
                return;
            }

            if (!this.hasValidUserId) {
                toast.error(
                    'Blocked user ID is required.'
                );
                return;
            }

            this.isSubmitting = true;

            try {
                const response =
                    await this.$store.dispatch(
                        'createBlockUserFun',
                        {
                            blocked_user:
                                this.blockedUserId
                        }
                    );

                /*
                 * Clear the currently selected room so the
                 * blocked user's chat does not remain open.
                 */
                bus.$emit('roomClearedBus', true);

                /*
                 * Refresh both chat-room collections after
                 * the block request succeeds.
                 */
                await this.refreshChatRooms();

                toast.success(
                    this.getSuccessMessage(
                        response,
                        'User blocked successfully.'
                    )
                );
            } catch (error) {
                toast.error(
                    this.getErrorMessage(
                        error,
                        'Unable to block the user.'
                    )
                );
            } finally {
                this.isSubmitting = false;
                this.closePopup();
            }
        },

        async refreshChatRooms() {
            const refreshRequests = [
                this.$store.dispatch(
                    'fetchPinnedChatRooms'
                ),
                this.$store.dispatch(
                    'fetchNormalChatRooms'
                )
            ];

            try {
                await Promise.all(refreshRequests);
            } catch (error) {
                /*
                 * Blocking has already succeeded, so a room
                 * refresh failure should not show the block
                 * operation as failed.
                 */
                console.error(
                    'Unable to refresh chat rooms:',
                    error
                );
            }
        },

        closePopup() {
            if (this.isSubmitting) {
                return;
            }

            bus.$emit('blockSelectUserBus', false);

            document.body.classList.remove(
                'body_right_scroll_remove'
            );

            this.restoreFocus();
        },

        restoreFocus() {
            this.$nextTick(() => {
                if (
                    this.previouslyFocusedElement &&
                    typeof this.previouslyFocusedElement
                        .focus === 'function'
                ) {
                    this.previouslyFocusedElement.focus();
                }

                this.previouslyFocusedElement = null;
            });
        },

        handleKeydown(event) {
            if (
                event.key === 'Escape' &&
                !this.isSubmitting
            ) {
                this.closePopup();
            }
        },

        getSuccessMessage(response, fallbackMessage) {
            if (
                response &&
                response.data
            ) {
                return (
                    response.data.msg ||
                    response.data.message ||
                    fallbackMessage
                );
            }

            return fallbackMessage;
        },

        getErrorMessage(error, fallbackMessage) {
            const responseData =
                error &&
                error.response &&
                error.response.data
                    ? error.response.data
                    : error && error.data
                        ? error.data
                        : null;

            if (!responseData) {
                return (
                    error &&
                    error.message
                        ? error.message
                        : fallbackMessage
                );
            }

            const nestedData =
                responseData.data;

            if (
                nestedData &&
                Array.isArray(
                    nestedData.non_field_errors
                ) &&
                nestedData.non_field_errors.length
            ) {
                return nestedData.non_field_errors[0];
            }

            if (
                Array.isArray(
                    responseData.non_field_errors
                ) &&
                responseData.non_field_errors.length
            ) {
                return responseData.non_field_errors[0];
            }

            return (
                responseData.msg ||
                responseData.message ||
                fallbackMessage
            );
        }
    }
};
</script>

<style lang="scss" scoped>
$primary-text: #ffffff;
$secondary-text: #a8a8a8;
$accent-color: #87cefa;
$danger-color: #ff3a30;
$highlight-color: #ffe738;
$dialog-background: #000000;
$overlay-background: rgba(0, 0, 0, 0.9);

.block-user-modal {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    overflow-y: auto;
    background-color: $overlay-background;

    &__dialog {
        width: 100%;
        max-width: 520px;
        overflow: hidden;
        border-radius: 6px;
        color: $primary-text;
        background-color: $dialog-background;
        box-shadow: 0 0 14px rgba(
            255,
            213,
            0,
            0.55
        );
        font-family:
            'MyriadProRegular',
            'Lato',
            sans-serif;
    }

    &__body {
        padding: 26px 20px 22px;
        text-align: center;
    }

    &__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 64px;
        height: 64px;
        margin-bottom: 16px;
        border-radius: 50%;
        color: $danger-color;
        background-color: rgba(
            255,
            58,
            48,
            0.12
        );
        font-size: 30px;
    }

    &__message {
        margin: 0;
        color: $primary-text;
        font-size: 18px;
        line-height: 1.5;

        strong {
            color: $accent-color;
            word-break: break-word;
        }
    }

    &__description {
        margin: 10px 0 0;
        color: $secondary-text;
        font-size: 14px;
        line-height: 1.5;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content:center;
        gap: 12px;
        padding: 0 20px 20px;
    }

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 110px;
        min-height: 40px;
        padding: 8px 14px;
        border: 0;
        border-radius: 4px;
        color: $primary-text;
        font-family: inherit;
        font-size: 16px;
        cursor: pointer;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            margin-right: 7px;
        }

        &:focus {
            outline: 2px solid $highlight-color;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        &--cancel {
            background-color: rgba(
                255,
                255,
                255,
                0.14
            );

            &:hover:not(:disabled){
                background-color: rgba(
                    255,
                    255,
                    255,
                    0.25
                );
            }
        }

        &--confirm {
            background-color: $danger-color;

            &:hover:not(:disabled) {
                color: #000000;
                background-color: $highlight-color;
            }
        }
    }
}

@media only screen and (max-width: 575px) {
    .block-user-modal {
        // align-items: flex-end;
        padding: 10px;

        &__dialog {
            max-width: 100%;
        }

        &__header {
            padding: 16px;
        }

        &__body {
            padding: 22px 16px;
        }

        &__footer {
            flex-direction: column-reverse;
            padding: 0 16px 16px;
        }

        &__button {
            width: 100%;
        }
    }
}
</style>